/**
 * statusAkaunKad.js — Status TikTok/Facebook per AKAUN kalendar, disimpan pada KAD.
 *
 * `kunciAkaun` + `gabungStatusAkaun` DIPINDAH dari WorkspaceView.jsx pada
 * 2026-09-18 tanpa diubah (kod asal 2026-08-23). Fungsi lain di bawah ialah
 * kerja baharu untuk butang "Status" pada bar pukal kad.
 *
 * Arahan user 2026-08-23: kad mesti menunjukkan set mana sudah dihantar ke
 * TikTok, dan status itu WAJIB kekal walaupun slot kalendar dipadam, akaun
 * ditukar, atau kad dipindah ke group lain.
 *
 * ⛔ Sebab ia perlu medan BAHARU, bukan `setStatuses` sedia ada:
 *    `setStatuses.tiktok_setN` hanya menyimpan SATU nilai bagi setiap set,
 *    dan robot menulis ganti nilai itu setiap kali set yang sama dijalankan.
 *    Jadi menghantar Set 1 ke Akaun 1 kemudian ke Akaun 2 akan memadam
 *    rekod Akaun 1 - sedangkan user minta SEMUA akaun diingat.
 *    Nama akaun pula tidak pernah wujud pada kad langsung; ia hidup dalam
 *    slot kalendar, iaitu benda yang user memang mahu boleh dipadam.
 *
 * Bentuk: setStatusAkaun.tiktok_set1 = { <kunciAkaun>: {st, sb, nama, masa} }
 *    (Larian Facebook dari kalendar pun direkod di bawah kunci `tiktok_` yang
 *    sama — kunci itu nama medan, bukan platform.)
 *
 * ⚠️ Status yang DIBUANG pengguna disimpan sebagai st: "" - BUKAN dengan
 *    membuang kuncinya. Subdoc produk ditulis dengan merge, dan Firestore
 *    menggabungkan peta secara DALAM, jadi kunci yang dibuang daripada objek
 *    tempatan akan hidup semula pada muat berikutnya. Corak sama seperti
 *    nota "DIKOSONGKAN, BUKAN DIBUANG" pada larian agent.
 *
 * ★ [2026-09-18] PIL IKUT AKAUN YANG DIBUKA (arahan user: "pil hanya appear
 *   status yang related dengan akaun yang dibuka di calendar").
 *   Pil pada kad kini memapar status AKAUN YANG SEDANG DIBUKA di kalendar
 *   sahaja, dan butang Status pada bar kad bekerja atas akaun yang SAMA.
 *   Rekod akaun lain KEKAL tersimpan — ia muncul semula bila akaun itu dibuka.
 *   Sebelum ini kad memapar satu baris bagi SETIAP akaun (bernombor), sedangkan
 *   butang pukal hanya mengosongkan akaun yang dibuka: pengguna nampak pil
 *   "Failed" milik akaun lain yang butang itu tidak boleh sentuh, dan butang
 *   itu diam (aduan user 2026-09-18).
 */

export const SET_KAD_STATUS = ['set1', 'set2', 'set3'];

/* ★ [2026-09-29] "POSTED" (arahan user: Post now mesti tunjuk "Posted" pada
   kad juga, dan boleh dipilih dalam menu Status).
   ⛔ "Posted" BUKAN nilai `st` baharu. Ia `st: 'complete'` + `md: 'now'` —
      bentuk yang sama dengan cop slot kalendar sejak 27 Sept. Robot hanya tahu
      'complete', dan banyak tempat menyemak 'complete' (graduasi, warna,
      kiraan); nilai baharu akan memecahkan semuanya secara senyap.
   'posted' hanya id PILIHAN dalam menu — ditukar di sini sahaja. */
export const ID_POSTED = 'posted';

/* id pilihan menu → { st, md } untuk disimpan. */
export function pecahPilihanStatus(v) {
    if (v === ID_POSTED) return { st: 'complete', md: 'now' };
    return { st: v || '', md: '' };
}

/* Rekod (kad atau slot) → id pilihan menu yang sepadan. */
export function idPilihanStatus(st, md) {
    return st === 'complete' && md === 'now' ? ID_POSTED : (st || '');
}

export const kunciAkaun = (id) => String(id || '').replace(/[^A-Za-z0-9_-]/g, '_') || 'akaun';

export function gabungStatusAkaun(peta, setKey, idAkaun, data) {
    const asalPeta = peta || {};
    const kSet = `tiktok_${setKey}`;
    const bagiSet = { ...(asalPeta[kSet] || {}) };
    const kA = kunciAkaun(idAkaun);
    bagiSet[kA] = { ...(bagiSet[kA] || {}), ...data };
    return { ...asalPeta, [kSet]: bagiSet };
}

/* Rekod status AKAUN YANG DIBUKA bagi satu set pada satu kad — atau null.
   `st` kosong = pengguna sudah membuangnya, jadi dikira tiada.
   ⛔ Tiada akaun = null (tiada pil), BUKAN "semua akaun". Memapar semua akaun
      ialah perangai lama yang ditarik balik pada 2026-09-18. */
export function statusAkaunDibuka(product, setKey, idAkaun) {
    if (!product || !idAkaun) return null;
    const kA = kunciAkaun(idAkaun);
    const r = product.setStatusAkaun?.[`tiktok_${setKey}`]?.[kA];
    return r && r.st ? { id: kA, ...r } : null;
}

/* Ada video untuk set ini? Status hanya bermakna untuk set yang ada video —
   "Scheduled" pada kotak kosong ialah data yang menipu. Medan media duduk
   dalam doc UTAMA (Split V2 hantar teks sahaja ke subdoc), jadi ia boleh
   dibaca tanpa hidrat. */
export function adaVideoSet(product, setKey) {
    return !!product?.prompts?.[setKey]?.uploadedVideo;
}

/* Kiraan untuk menu butang Status: berapa kad yang BOLEH disentuh bagi setiap
   set, untuk akaun yang dibuka = kad yang ada video untuk set itu ATAU sudah
   ada pil untuk akaun itu. 'running' tidak dikira — set yang sedang dihantar
   tidak boleh diubah (guna Stop).

   ⭐ [2026-09-20] Dahulu ia mengira pil SEDIA ADA sahaja, jadi set tanpa pil
      langsung tidak muncul dalam menu (soalan user: "kenapa tak ada untuk set
      2 dan 3?"). Sejak pengguna boleh MENANDA status sendiri pada banyak kad,
      kiraan mesti mengikut apa yang tindakan itu akan sentuh. */
export function kiraStatusAkaun(kadTerpilih, idAkaun) {
    const c = { set1: 0, set2: 0, set3: 0, jumlah: 0 };
    (kadTerpilih || []).forEach((p) => {
        SET_KAD_STATUS.forEach((s) => {
            const r = statusAkaunDibuka(p, s, idAkaun);
            if (r && r.st === 'running') return;
            if (r || adaVideoSet(p, s)) { c[s]++; c.jumlah++; }
        });
    });
    return c;
}

/* Rancangan tukar status — TULEN, tiada tulisan. SATU tempat untuk peraturan
   status, dikongsi DUA bar: bar pukal kad (senarai kiri, tab Schedule) dan bar
   pukal slot (kalendar). Bar kad menghantar kad × set; bar kalendar menghantar
   satu pasangan bagi setiap slot yang ditanda (slot = satu kad + satu set).
   ⛔ Jangan salin peraturan ini ke tempat kedua — ubah di sini sahaja.
   `stBaru` '' = kosongkan (Not run yet).
   Pulang { tukar, sama, berjalan, tiadaVideo, tiadaStatus }:
     tukar       = rekod untuk `tulisStatusAkaunKad` / `cerminStatusKeSlot`
     sama        = pil yang memang sudah bernilai `stBaru` (tiada apa diubah)
     berjalan    = pil 'running' yang dilangkau (guna Stop)
     tiadaVideo  = kad dilangkau kerana set itu tiada video (menanda sahaja)
     tiadaStatus = kad dilangkau semasa MENGOSONGKAN kerana memang tiada pil
     tiadaKad    = pasangan dilangkau kerana kadnya tiada dalam senarai (slot
                   yang kadnya sudah dipadam) — dilapor, bukan disenyapkan

   ⭐ [2026-09-20] MENANDA status baharu DIBENARKAN (arahan user: "kadang user
      nak juga tanda sendiri status kat multiple card") — contohnya pelanggan
      yang post sendiri ke TikTok, kemudian mahu kad menunjukkannya.
   ⛔ Tetapi hanya untuk set yang ADA VIDEO. Kad tanpa video untuk set itu
      dilangkau dan DILAPOR — status di bawah kotak kosong ialah rekod palsu.
      Pil yang sudah wujud tetap boleh ditukar walaupun videonya sudah dipadam.
   ⛔ Mengosongkan ('') TIDAK mencipta apa-apa — ia hanya membuang pil sedia ada. */
export function pelanStatusPasangan(pasangan, idAkaun, stBaru, namaAkaun) {
    /* `stBaru` boleh jadi 'posted' (id menu) — lihat pecahPilihanStatus. */
    const { st, md } = pecahPilihanStatus(stBaru);
    const tukar = [];
    let sama = 0;
    let berjalan = 0;
    let tiadaVideo = 0;
    let tiadaStatus = 0;
    let tiadaKad = 0;
    (pasangan || []).forEach((x) => {
        const kad = x?.kad;
        const s = x?.s;
        if (!kad || !s) { tiadaKad++; return; }
        const r = statusAkaunDibuka(kad, s, idAkaun);
        if (r && r.st === 'running') { berjalan++; return; }
        if (!r) {
            if (st === '') { tiadaStatus++; return; }
            if (!adaVideoSet(kad, s)) { tiadaVideo++; return; }
        } else if (r.st === st && (r.md || '') === md) { sama++; return; }
        tukar.push({ pid: kad.id, s, akaunId: idAkaun, st, sb: '', md, nama: namaAkaun || '' });
    });
    return { tukar, sama, berjalan, tiadaVideo, tiadaStatus, tiadaKad };
}

/* Bar pukal KAD: setiap kad ditanda × setiap set yang dipilih. Ia hanya
   membina senarai pasangan, kemudian menyerahkannya kepada peraturan di atas —
   jadi bar kad dan bar kalendar berkongsi peraturan yang SAMA. */
export function pelanStatusPukal(kadTerpilih, sets, idAkaun, stBaru, namaAkaun) {
    const pasangan = [];
    (kadTerpilih || []).forEach((kad) => {
        (sets || []).forEach((s) => pasangan.push({ kad, s }));
    });
    return pelanStatusPasangan(pasangan, idAkaun, stBaru, namaAkaun);
}
